import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import LexCounselMark from "./LexCounselMark";

const RETRACT_DELAY_MS = 3000;
/** Icon-only chip when retracted */
const COMPACT_WIDTH_PX = 56;
const COMPACT_HEIGHT_PX = 38;
const COMPACT_RADIUS_PX = 12;
const EXPANDED_HEIGHT_PX = 42;
const EXPANDED_RADIUS_PX = 20;
/** Wordmark + AI badge — fixed width for smooth width animation */
const EXPANDED_WIDTH_PX = 224;

const WIDTH_EASE = [0.32, 0.72, 0, 1] as const;
const WIDTH_TRANSITION = {
  duration: 0.42,
  ease: WIDTH_EASE,
};

const LAUNCHER_SHELL =
  "border-0 bg-[#141414] p-0 outline-none ring-0 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 [transform:translateZ(0)]";

type LexCounselLauncherProps = {
  /** When true, parent may hide the launcher; kept for callers that still pass it. */
  isPaneOpen?: boolean;
  onClick: () => void;
  className?: string;
  active?: boolean;
};

/**
 * Lex Counsel AI entry — brand lockup on dark chip; compact shows icon + AI badge.
 */
export default function LexCounselLauncher({
  isPaneOpen = false,
  onClick,
  className = "",
  active = false,
}: LexCounselLauncherProps) {
  const [retracted, setRetracted] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    setRetracted(false);
    setHovered(false);
    const timer = window.setTimeout(() => setRetracted(true), RETRACT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  if (isPaneOpen) return null;

  const expanded = !retracted || hovered;
  const isCompact = retracted && !hovered;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className={`relative inline-flex shrink-0 cursor-pointer select-none items-center justify-center overflow-hidden shadow-[0_8px_28px_rgba(0,0,0,0.22)] transition-shadow duration-200 hover:shadow-[0_10px_32px_rgba(0,0,0,0.28)] ${LAUNCHER_SHELL} ${className}`}
      style={{ transformOrigin: "center center" }}
      initial={{
        width: EXPANDED_WIDTH_PX,
        height: EXPANDED_HEIGHT_PX,
        borderRadius: EXPANDED_RADIUS_PX,
        opacity: 1,
      }}
      animate={{
        width: expanded ? EXPANDED_WIDTH_PX : COMPACT_WIDTH_PX,
        height: expanded ? EXPANDED_HEIGHT_PX : COMPACT_HEIGHT_PX,
        borderRadius: expanded ? EXPANDED_RADIUS_PX : COMPACT_RADIUS_PX,
        opacity: active ? [1, 0.88, 1] : 1,
      }}
      transition={{
        width: WIDTH_TRANSITION,
        height: WIDTH_TRANSITION,
        borderRadius: WIDTH_TRANSITION,
        opacity: active
          ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
          : { duration: 0.2 },
      }}
      whileTap={{ opacity: 0.92 }}
      aria-label="Open Lex Counsel AI"
    >
      <LexCounselMark
        variant={isCompact ? "icon" : "wordmark"}
        size={isCompact ? "sm" : "md"}
        onDarkChip={false}
        showAiBadge
        className={isCompact ? "px-2 py-1" : "px-2.5 py-1.5"}
      />
    </motion.button>
  );
}
